import * as Sentry from "@sentry/react";
import { isLocalHost } from "myParkdUtils/utils";
import { useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';
import { ResponseError } from "superagent";
import { Company, User } from "types";


// We block Sentry on localhost to avoid cluttering the logs
// for testing purposes you can change this behaviour below
// do not forget to set "Allowed Domains" to "*" in MyParkd Project in Sentry (for localhost)
if (!isLocalHost()) {
    Sentry.init({
        release: `my-parkd@${import.meta.env.VITE_RELEASE_VERSION}`,
        dsn: 'https://db2c533c3720473398cf7a9b17bed7b6@o90410.ingest.sentry.io/5739307',
        integrations: [
            Sentry.reactRouterV7BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes
            })
        ],
        // Sentry v11 collects user info, cookies, headers, query params and http bodies (e.g. login passwords) by default.
        // These settings keep v10's default behaviour of not sending PII.
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: false,
            urlQueryParams: true,
            httpBodies: [],
            genAI: { inputs: false, outputs: false },
            databaseQueryData: false,
            queues: false,
            stackFrameVariables: true
        },
        normalizeDepth: 5, // Or however deep you want your state context to be (default=3)
        tracesSampleRate: 0.1,
        //tracePropagationTargets: ["my.parkd.com", "dev-my.parkd.com"],
        maxBreadcrumbs: 50,
        environment: import.meta.env.VITE_ENVIRONMENT === 'prod' ? 'production' : 'development',
        debug: false // for testing localhost
    });
}


export const setSentryScope = (token: string, user: User, company: Company) => {
    if (token) {
        try {
            Sentry.getCurrentScope().setUser({
                id: user?.id ?? 'N/A',
                username: `${user?.givenName} ${user?.familyName}`,
                email: user?.email ?? 'N/A'
            });
            Sentry.getCurrentScope().setTags({
                companyId: company.id ?? 'N/A',
                companyName: company.name ?? 'N/A'
            });
        } catch (e) {
            console.error(e);
        }
    } else {
        console.warn('setSentryScope was called but token was undefined');
    }
}

export const isSuperagentErrorResponse = (error: ResponseError | any): boolean => {
    return error.hasOwnProperty('status') && error.hasOwnProperty('response');
}

export const captureMessageWithError = (message: string, error: any) => {
    const extra: Record<string, unknown> = isSuperagentErrorResponse(error)
        ? {
            error: error?.response?.error
        } : {
            error
        };
    Sentry.captureMessage(message, { extra });
}
